import {
  CalendarClockIcon,
  CheckmarkCircle02Icon,
  ComputerIcon,
  Download01Icon,
  Globe02Icon,
  LicenseIcon,
  PackageReceiveIcon,
  Shield02Icon,
  UserGroupIcon,
} from '@hugeicons/core-free-icons';
import type { IconSvgElement } from '@hugeicons/react';
import { useState, type ReactNode } from 'react';
import { HugeIcon } from '../../components/ui/HugeIcon';
import { StatusPill } from '../../components/ui/StatusPill';
import { Button } from '../../components/ui/Button';
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { downloadLicenseFile, type ConsoleState } from '../../lib/api';
import { getLicensePeriodProgress } from '../../lib/licensePeriod';
import { SegmentedMeter } from '../../components/layout/SegmentedMeter';

export function LicensePage({ license, organization, releases, usedSeats }: { license: ConsoleState['license']; organization: ConsoleState['organization']; releases: ConsoleState['releases']; usedSeats: number }) {
  const expiry = getLicensePeriodProgress(license?.startDate, license?.expiryDate);
  const [busyDomain, setBusyDomain] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const domains = license?.licensedDomains.length ? license.licensedDomains : [organization.domain];
  const isWorkosDeployment = license?.deploymentModel === 'saas_hybrid';

  async function download(domain: string) {
    setBusyDomain(domain);
    setMessage(null);
    try {
      await downloadLicenseFile(domain);
      setMessage(`Download started for ${domain}.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Download failed.');
    } finally {
      setBusyDomain(null);
    }
  }

  return (
    <section className="flex flex-col gap-4">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <LicenseMetricCard
          detail={`Current access for ${organization.name}`}
          icon={Shield02Icon}
          iconTone="bg-emerald-500/12 text-emerald-700"
          label="Status"
          value={<StatusPill className="w-fit" status={license?.status ?? 'unknown'} />}
        />
        <LicenseMetricCard
          detail={`${license?.startDate ?? 'Not set'} — ${license?.expiryDate ?? 'Not set'}`}
          icon={CalendarClockIcon}
          iconTone="bg-violet-500/12 text-violet-700"
          label="License period"
          value={expiry.label}
        >
          <SegmentedPeriodMeter value={expiry.percent} />
        </LicenseMetricCard>
        <LicenseMetricCard
          detail={`${Math.max(0, (license?.seatLimit ?? 0) - usedSeats)} seats available`}
          icon={UserGroupIcon}
          iconTone="bg-teal-500/12 text-teal-700"
          label="Licensed seats"
          value={<span className="flex items-baseline justify-between text-sm font-normal"><span className="text-muted-foreground">Seats used</span><span className="font-semibold tabular-nums">{usedSeats} <span className="font-normal text-muted-foreground">of {license?.seatLimit ?? 0}</span></span></span>}
        >
          <SegmentedMeter ariaLabel={`${usedSeats} of ${license?.seatLimit ?? 0} seats used`} ratio={(license?.seatLimit ?? 0) > 0 ? usedSeats / (license?.seatLimit ?? 1) : 0} />
        </LicenseMetricCard>
        <LicenseMetricCard
          detail={releases[0]?.version ? `Latest build ${releases[0].version}` : 'No build published'}
          icon={LicenseIcon}
          iconTone="bg-sky-500/12 text-sky-700"
          label="Release channel"
          value={<span className="capitalize">{license?.releaseChannel ?? 'stable'}</span>}
        />
      </div>

      <Card className="gap-0 py-0" size="sm">
        <CardHeader className="p-5 pb-4">
          <CardTitle>{isWorkosDeployment ? 'Managed identity access' : 'Domain license files'}</CardTitle>
          <CardDescription>{isWorkosDeployment ? 'This deployment uses managed identity and does not require license.jwt.' : 'Download a current signed license for each deployment.'}</CardDescription>
          <CardAction><span className="text-xs text-muted-foreground">{domains.length} {domains.length === 1 ? 'domain' : 'domains'}</span></CardAction>
        </CardHeader>

        <CardContent className="space-y-3 px-5 pb-5">
          {!isWorkosDeployment ? <div className={domains.length > 1 ? 'grid gap-3 sm:grid-cols-2' : 'grid gap-3'}>
            {domains.map((domain) => (
              <div className="flex min-h-14 items-center gap-3 rounded-lg border bg-background px-3 py-2.5" key={domain}>
                <HugeIcon className="shrink-0 text-muted-foreground" icon={PackageReceiveIcon} size={18} />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{domain}</p>
                  <p className="text-xs text-muted-foreground">Valid until {license?.expiryDate ?? 'not set'}</p>
                </div>
                <Button
                  aria-label={`Download license for ${domain}`}
                  disabled={busyDomain === domain || !license?.downloadAvailable}
                  onClick={() => void download(domain)}
                  size="sm"
                  type="button"
                  variant="outline"
                >
                  <HugeIcon icon={Download01Icon} size={15} />
                  {busyDomain === domain ? 'Preparing…' : 'Download'}
                </Button>
              </div>
            ))}
          </div> : null}

          <div className="grid divide-y rounded-lg border bg-background px-3 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
            <CoverageRow icon={Shield02Icon} label="Admin console" value="Included" />
            <CoverageRow icon={ComputerIcon} label={isWorkosDeployment ? 'Managed authentication' : 'On-prem PDF processing'} value="Included" />
            <CoverageRow icon={Globe02Icon} label="Authorized domains" value={String(domains.length)} />
          </div>

          <div className="flex items-center gap-2 rounded-lg bg-muted px-3 py-2.5 text-xs text-muted-foreground">
            <HugeIcon icon={CheckmarkCircle02Icon} size={16} />
            {isWorkosDeployment ? <>User access is managed through the identity service; deploy the matching <span className="font-medium text-foreground">SaaS / Hybrid</span> release package.</> : <>Every download is generated as <span className="font-medium text-foreground">license.jwt</span> and expires on {license?.expiryDate ?? 'the configured date'}.</>}
          </div>

          {message ? <p aria-live="polite" className="text-xs text-muted-foreground">{message}</p> : null}
        </CardContent>
      </Card>
    </section>
  );
}

function LicenseMetricCard({ children, detail, icon, iconTone, label, value }: { children?: ReactNode; detail: string; icon: IconSvgElement; iconTone: string; label: string; value: ReactNode }) {
  return (
    <Card className="h-full gap-3 py-3" size="sm">
      <CardContent className="flex h-full flex-col gap-3">
        <div className="flex items-center gap-2">
          <span className={`flex size-7 shrink-0 items-center justify-center rounded-lg ${iconTone}`}><HugeIcon icon={icon} size={15} strokeWidth={1.9} /></span>
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
        </div>
        <div className="text-2xl font-semibold tracking-tight">{value}</div>
        {children}
        <p className="mt-auto text-xs text-muted-foreground">{detail}</p>
      </CardContent>
    </Card>
  );
}

function CoverageRow({ icon, label, value }: { icon: IconSvgElement; label: string; value: string }) {
  return (
    <div className="flex min-h-11 items-center gap-3 py-2 sm:px-3 first:pl-0 last:pr-0">
      <HugeIcon className="shrink-0 text-muted-foreground" icon={icon} size={17} />
      <span className="min-w-0 flex-1 truncate text-sm">{label}</span>
      <span className="text-xs font-medium text-foreground">{value}</span>
    </div>
  );
}

function SegmentedPeriodMeter({ value }: { value: number }) {
  return <SegmentedMeter ariaLabel={`${value}% of the license period remaining`} colorClassName="bg-violet-600" ratio={value / 100} />;
}
